/**
 * Canonical forecast-version dropdown order for all reports:
 * Live EAC → Budget → Q1 → Q2 → Q3 → Q4 → EAC 11 → EAC 10 … EAC 2
 */

export const VERSION_SORT_KEYS = [
  "liveEac",
  "budget",
  "q1",
  "q2",
  "q3",
  "q4",
  "eac11",
  "eac10",
  "eac9",
  "eac8",
  "eac7",
  "eac6",
  "eac5",
  "eac4",
  "eac3",
  "eac2",
  "eac1",
];

/** Collapse key/label variants to a comparable token (e.g. "EAC 11" → "eac11"). */
export function normalizeVersionToken(raw) {
  let s = String(raw ?? "")
    .toLowerCase()
    .trim()
    .replace(/[\s_-]+/g, "");
  if (!s) return "";
  if (s === "live" || s === "liveeac") return "liveeac";
  if (s.startsWith("budget")) return "budget";
  return s;
}

function versionKeyOf(item) {
  if (item == null) return "";
  if (typeof item === "string") return item;
  return item.key ?? item.value ?? item.id ?? item.name ?? item.label ?? "";
}

function rankOf(raw) {
  const token = normalizeVersionToken(raw);
  if (!token) return Number.MAX_SAFE_INTEGER;
  const idx = VERSION_SORT_KEYS.findIndex(
    (k) => normalizeVersionToken(k) === token,
  );
  return idx >= 0 ? idx : VERSION_SORT_KEYS.length + 100;
}

/**
 * Sort a list of version objects or strings into the canonical dropdown order.
 * Unknown versions keep relative order after known ones (stable by name).
 */
export function sortVersions(list) {
  if (!Array.isArray(list) || list.length === 0) return list || [];
  return [...list]
    .map((item, i) => ({ item, i, rank: rankOf(versionKeyOf(item)) }))
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      const an = String(versionKeyOf(a.item));
      const bn = String(versionKeyOf(b.item));
      const cmp = an.localeCompare(bn, undefined, { sensitivity: "base" });
      return cmp !== 0 ? cmp : a.i - b.i;
    })
    .map(({ item }) => item);
}

function indirectVersionLabelOf(item) {
  if (item == null) return "";
  if (typeof item === "string") return item;
  return (
    item.name ??
    item.version_name ??
    item.versionName ??
    item.label ??
    item.key ??
    item.id ??
    item.version ??
    ""
  );
}

/** Year EAC → Budget → plain EAC (e.g. 2026 EAC 6, Budget 2026, EAC 7). */
function indirectVersionSortKey(label) {
  const s = String(label ?? "").trim();
  const lower = s.toLowerCase();

  const yearEacMatch = lower.match(/^(\d{4})\s+.*?\beac\s*(\d+)/);
  if (yearEacMatch) {
    const year = parseInt(yearEacMatch[1], 10);
    const num = parseInt(yearEacMatch[2], 10);
    return { type: 0, year, num, label: s };
  }

  const budgetMatch = lower.match(/budget(?:\s*(\d{4}))?/);
  if (budgetMatch) {
    const year = budgetMatch[1] ? parseInt(budgetMatch[1], 10) : 0;
    return { type: 1, year, num: 0, label: s };
  }

  const eacMatch = lower.match(/\beac\s*(\d+)/);
  if (eacMatch) {
    const num = parseInt(eacMatch[1], 10);
    return { type: 2, year: 0, num, label: s };
  }

  return { type: 3, year: 0, num: 0, label: s };
}

/**
 * Sort indirect-report version dropdowns:
 * 2026 EAC 6 → Budget 2026 → EAC 7 → …
 */
export function sortIndirectVersions(list) {
  if (!Array.isArray(list) || list.length === 0) return list || [];
  return [...list]
    .map((item, i) => ({
      item,
      i,
      key: indirectVersionSortKey(indirectVersionLabelOf(item)),
    }))
    .sort((a, b) => {
      const ka = a.key;
      const kb = b.key;
      if (ka.type !== kb.type) return ka.type - kb.type;

      if (ka.type === 0 || ka.type === 1) {
        if (ka.year !== kb.year) return kb.year - ka.year;
      }
      if (ka.type === 0 || ka.type === 2) {
        if (ka.num !== kb.num) return kb.num - ka.num;
      }

      const cmp = ka.label.localeCompare(kb.label, undefined, {
        sensitivity: "base",
      });
      return cmp !== 0 ? cmp : a.i - b.i;
    })
    .map(({ item }) => item);
}

/** Fallback options already in canonical order (for offline / loading UI). */
export const VERSION_OPTIONS_FALLBACK = [
  { value: "liveEac", label: "Live EAC" },
  { value: "budget", label: "Budget" },
  { value: "q1", label: "Q1" },
  { value: "q2", label: "Q2" },
  { value: "q3", label: "Q3" },
  { value: "q4", label: "Q4" },
  { value: "eac11", label: "EAC 11" },
  { value: "eac10", label: "EAC 10" },
  { value: "eac9", label: "EAC 9" },
  { value: "eac8", label: "EAC 8" },
  { value: "eac7", label: "EAC 7" },
  { value: "eac6", label: "EAC 6" },
  { value: "eac5", label: "EAC 5" },
  { value: "eac4", label: "EAC 4" },
  { value: "eac3", label: "EAC 3" },
  { value: "eac2", label: "EAC 2" },
];
